import {
  MAX_SCHEDULED_MESSAGES_PER_DAY,
  MAX_SCHEDULED_MESSAGES_SENT_PER_DAY,
} from '@act/shared/constants';
import {
  MessageDto,
  MessageForm,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { useEffect, useState } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { messagesRequests } from 'redux/requests/messages.requests';
import {
  createMessageSuccess,
  updateMessageSuccess,
} from 'redux/slices/messagesSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import {
  getSchedulingLimitKind,
  resolveScheduledSendAt,
} from '../messageDialog.util';
import { UseMessageAttachmentsResult } from './useMessageAttachments.hook';

interface UseMessageSubmissionInput {
  user: UserDto;
  message?: MessageDto;
  scheduledEventId?: number;
  attachments: UseMessageAttachmentsResult;
  closeDialog: (success?: boolean) => void;
}

interface ScheduleSelection {
  scheduleDate: Date;
  scheduleTime: number;
}

/**
 * Sends or schedules the text message: uploads staged attachments, calls the
 * create/update endpoints, and reports success or quota failures.
 * @param input - Member, message being edited, attachment state, and close handler.
 * @returns Submission handlers plus in-flight and error state for the dialog.
 */
export const useMessageSubmission = ({
  user,
  message,
  scheduledEventId = null,
  attachments,
  closeDialog,
}: UseMessageSubmissionInput) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createdMessage, loadingCreateMessage, createMessage] = useApiCall(
    messagesRequests.createMessage
  );
  const [updatedMessage, loadingUpdateMessage, updateMessage] = useApiCall(
    messagesRequests.updateMessage
  );

  useEffect(() => {
    const savedMessage = createdMessage ?? updatedMessage;
    if (!savedMessage) {
      return;
    }
    dispatch(
      createdMessage
        ? createMessageSuccess(createdMessage)
        : updateMessageSuccess(updatedMessage)
    );
    toast(
      savedMessage.sendAt
        ? 'Message scheduled successfully'
        : 'Message sent successfully',
      'success'
    );
    closeDialog(true);
  }, [createdMessage, updatedMessage]);

  const submit = (data: MessageForm, fileIds: string[], sendAt?: number) => {
    if (message) {
      updateMessage({
        params: { messageId: message.id },
        body: {
          text: data.message,
          fileIds,
          phoneNumberId: data.phoneNumberId,
          ...(sendAt !== undefined && { sendAt }),
        },
      });
      return;
    }
    createMessage({
      params: null,
      body: {
        subjectId: user.id,
        text: data.message,
        fileIds,
        phoneNumberId: data.phoneNumberId,
        scheduledEventId,
        ...(sendAt !== undefined && { sendAt }),
      },
    });
  };

  const reportFailure = (submitError: unknown, isScheduled: boolean) => {
    const limitKind = getSchedulingLimitKind(submitError);
    if (limitKind === 'perDay') {
      toast(
        `Scheduling limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day.`,
        'error'
      );
    } else if (limitKind === 'sentPerDay') {
      toast(
        `Daily scheduled message limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent per day. Manual messages are unlimited.`,
        'error'
      );
    } else if (isScheduled) {
      toast(
        message ? 'Failed to update message' : 'Failed to schedule message',
        'error'
      );
    } else {
      setError(message ? 'Failed to update message' : 'Failed to send message');
    }
    closeDialog(false);
  };

  const sendNow = async (data: MessageForm) => {
    const upload = await attachments.uploadAttachments({ trackProgress: true });
    if (upload.status === 'aborted') {
      return;
    }
    if (upload.status === 'failed') {
      setError('Failed to upload one or more files. Please try again.');
      return;
    }

    setIsSending(true);
    try {
      submit(data, upload.fileIds);
    } catch (submitError) {
      reportFailure(submitError, false);
    } finally {
      setIsSending(false);
    }
  };

  const scheduleSend = async (
    data: MessageForm,
    selection: ScheduleSelection
  ) => {
    const sendAt = resolveScheduledSendAt(
      selection.scheduleDate,
      selection.scheduleTime,
      message?.sendAt
    );

    const upload = await attachments.uploadAttachments();
    if (upload.status !== 'ok') {
      toast('Failed to upload files. Please try again.', 'error');
      closeDialog(false);
      return;
    }

    try {
      submit(data, upload.fileIds, sendAt);
    } catch (submitError) {
      reportFailure(submitError, true);
    }
  };

  return {
    isSending,
    isSaving: loadingCreateMessage || loadingUpdateMessage,
    error,
    setError,
    clearError: () => setError(null),
    sendNow,
    scheduleSend,
  };
};
